/**
 * Custom Search View for ESS Design System
 * Provides a clean, simple search interface with input field and paginated results
 */
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { Helmet, flattenToAppURL } from '@plone/volto/helpers';
import { UniversalLink } from '@plone/volto/components';
import { ContentWide, ContentWrapper, PageSection } from 'volto-essvoltocomponents/components/Layout';
import Pagination from '../../Molecules/Pagination/Pagination';
import { Icon } from '@plone/volto/components';
import zoomSVG from '@plone/volto/icons/zoom.svg';
import { PageHeaderBackground } from 'volto-essvoltocomponents/components/Molecules';
import { TextField } from '../../Atoms/ESSTextField/TextField';
import { ESSButton, RichText } from 'volto-essvoltocomponents/components/Atoms';
import heroImage from '../../../images/default_hero.png';
import { buildSearchOptions } from './searchUtils';
import './SearchView.scss';

const messages = defineMessages({
  searchTitle: {
    id: 'Search Results',
    defaultMessage: 'Search Results',
  },
  searchPlaceholder: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  itemsMatching: {
    id: '{count} items matching your search terms',
    defaultMessage: '{count} items matching your search terms',
  },
  readMore: {
    id: 'READ MORE',
    defaultMessage: 'READ MORE',
  },
  noResultsTitle: {
    id: 'Ups... we couldn\'t find what you were looking for',
    defaultMessage: 'Ups... we couldn\'t find what you were looking for',
  },
  noResultsSubtitle: {
    id: 'But we thought you might find these interesting:',
    defaultMessage: 'But we thought you might find these interesting:',
  },
  ourWork: {
    id: 'OUR WORK',
    defaultMessage: 'OUR WORK',
  },
  dissemination: {
    id: 'DISSEMINATION',
    defaultMessage: 'DISSEMINATION',
  },
  publications: {
    id: 'PUBLICATIONS',
    defaultMessage: 'PUBLICATIONS',
  },
  news: {
    id: 'NEWS',
    defaultMessage: 'NEWS',
  },
});

/**
 * SearchView Component
 */
const SearchView = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get search query and page from URL
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get('SearchableText') || '';
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [searchText, setSearchText] = useState(initialQuery);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const itemsPerPage = 10;

  // Cache key per query + page: reused results are read from Redux without refetching
  const subrequestKey = `search-${initialQuery}-${currentPage}`;

  // Get search results from Redux store
  const searchResults = useSelector(
    (state) => state.search?.subrequests?.[subrequestKey],
  );

  const items = searchResults?.items || [];
  const total = searchResults?.total || 0;
  const loading = searchResults?.loading || false;
  const loaded = searchResults?.loaded || false;

  // Calculate pagination
  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  // Perform search when query or page changes
  useEffect(() => {
    const searchOptions = buildSearchOptions({
      query: initialQuery,
      currentPage,
      itemsPerPage,
    });

    if (!loaded) {
      dispatch(searchContent('/', searchOptions, subrequestKey));
    }
  }, [dispatch, initialQuery, currentPage, itemsPerPage, subrequestKey, loaded]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    if (searchText.trim()) {
      history.push(`/search?SearchableText=${encodeURIComponent(searchText)}`);
    } else {
      history.push('/search');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    // Handle both native events and direct value changes from @plone/components
    const value = e?.target?.value ?? e;
    setSearchText(value);
    if (value === '') {
      setCurrentPage(1);
      history.push('/search');
    }
  };

  // Backend already filters by portal_type; keep local var for consistency
  const filteredItems = items;

  // Extract plain text from short_description (richtext field)
  const getPlainTextDescription = (item) => {
    const shortDesc = item.short_description;
    if (!shortDesc) return '';

    // If it's already a string, return it
    if (typeof shortDesc === 'string') {
      // Remove HTML tags
      const text = shortDesc.replace(/<[^>]*>/g, '');
      // Crop to 160 characters
      return text.length > 160 ? text.substring(0, 160) + '...' : text;
    }

    // If it's an object with data property (Slate format)
    if (shortDesc.data) {
      const text = shortDesc.data.replace(/<[^>]*>/g, '');
      return text.length > 160 ? text.substring(0, 160) + '...' : text;
    }

    return '';
  };

  const search_input =  
    <div className="search-controls">
      <div className="search-form">
        <form onSubmit={handleSearchSubmit}>
          <TextField
            name="search"
            type="text"
            endIcon={<Icon name={zoomSVG}/>}
            value={searchText}
            onChange={handleInputChange}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            size="sm"
          />
        </form>
      </div>
      {!loading && total > 0 && (
      <div className="search-count">
        {intl.formatMessage(messages.itemsMatching, {
          count: total,
        })}
      </div>
      )}
  </div>;


  return (
    <div className="search-view">
      <Helmet title={intl.formatMessage(messages.searchTitle)} />
      <PageHeaderBackground
        className="search-view-header"
        variant="default"
        title={intl.formatMessage(messages.searchTitle)}
        backgroundImage={heroImage}
        children = {search_input}
        blurBg = {true}
      />
      <PageSection paddingBottom = "var(--ess-space-7xl)">
        <ContentWrapper>
          <ContentWide>
          
            {loading && (
              <div className="search-loading">
                <p>Searching...</p>
              </div>
            )}

            {!loading && (
              <>
                {filteredItems.length > 0 ? (
                  <>
                    <div className="search-results-list">
                      {filteredItems.map((item, index) => (
                        <article key={index} className="search-result-item">
                          <h3 className="search-result-item__title">
                            <UniversalLink href={item['@id']}>
                              {item.title || item.Title}
                            </UniversalLink>
                          </h3>

                          {getPlainTextDescription(item) && (
                            <RichText 
                              className="search-result-item__description" 
                              content = {getPlainTextDescription(item)}/>
                          )}
                          <ESSButton
                            variant="link-icon"
                            href={flattenToAppURL(item['@id'])}
                            className="search-result-item__link"
                            alignment="left"
                          >
                            {intl.formatMessage(messages.readMore)}
                          </ESSButton>
                        </article>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <ContentWide  
                        className="search-pagination" 
                        alignment="center">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </ContentWide>
                    )}
                  </>
                ) : (
                  initialQuery && (
                    <div className="search-no-results">
                      <h3 className="no-results-title">
                        {intl.formatMessage(messages.noResultsTitle)} 😞
                      </h3>
                      <h3 className="no-results-subtitle">
                        {intl.formatMessage(messages.noResultsSubtitle)}
                      </h3>

                      <div className="useful-links">
                        <div className="useful-link-section">
                          <h3 className="search-result-item__title">
                            <UniversalLink href="/our-work">
                              {intl.formatMessage(messages.ourWork)}
                            </UniversalLink>
                          </h3>
                          <ESSButton
                            variant="link-icon"
                            href="/our-work"
                            className="search-result-item__link"
                            alignment="left"
                          >
                            {intl.formatMessage(messages.ourWork)}
                          </ESSButton>
                        </div>

                        <div className="useful-link-section">
                          <h3 className="search-result-item__title">
                            <UniversalLink href="/resources/dissemination">
                              {intl.formatMessage(messages.dissemination)}
                            </UniversalLink>
                          </h3>
                          <ESSButton
                            variant="link-icon"
                            href="/resources/dissemination"
                            className="search-result-item__link"
                            alignment="left"
                          >
                            {intl.formatMessage(messages.dissemination)}
                          </ESSButton>
                        </div>

                        <div className="useful-link-section">
                          <h3 className="search-result-item__title">
                            <UniversalLink href="/resources/publications">
                              {intl.formatMessage(messages.publications)}
                            </UniversalLink>
                          </h3>
                          <ESSButton
                            variant="link-icon"
                            href="/resources/publications"
                            className="search-result-item__link"
                            alignment="left"
                          >
                            {intl.formatMessage(messages.publications)}
                          </ESSButton>
                        </div>

                        <div className="useful-link-section">
                          <h3 className="search-result-item__title">
                            <UniversalLink href="/news">
                              {intl.formatMessage(messages.news)}
                            </UniversalLink>
                          </h3>
                          <ESSButton
                            variant="link-icon"
                            href="/news"
                            className="search-result-item__link"
                            alignment="left"
                          >
                            {intl.formatMessage(messages.news)}
                          </ESSButton>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </>
            )}
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default SearchView;
